/**
 * Service Worker 등록과 갱신 알림.
 *
 * 개발 중에는 등록하지 않는다 — 캐시가 살아 있으면 방금 고친 코드가 아니라
 * 아까 것을 보게 되고, 그 원인을 찾는 데 시간이 다 간다.
 */
export type PwaUpdate = { waiting: ServiceWorker } | null;

export function registerServiceWorker(onUpdate: (u: PwaUpdate) => void): void {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return;

  /*
   * `load` 를 기다리되, **이미 끝났으면 기다리지 않는다.**
   *
   * 이 함수는 React 의 effect 에서 불린다. 빠른 환경(로컬·캐시된 방문·빠른 회선)
   * 에서는 effect 가 도는 시점에 `load` 가 이미 지나가 있고, 그러면 리스너는
   * 영영 호출되지 않는다 = **Service Worker 가 등록되지 않는다.**
   * 느린 환경에서는 등록되므로, 환경에 따라 되기도 안 되기도 하는 종류의 결함이다.
   * 프로덕션 빌드를 띄워 놓고 `document.readyState` 가 이미 "complete" 인 것을
   * 확인하고 고쳤다.
   */
  const start = () => {
    void navigator.serviceWorker.register("/sw.js").then((reg) => {
      if (reg.waiting) onUpdate({ waiting: reg.waiting });

      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener("statechange", () => {
          /* controller 가 있다는 건 이미 쓰고 있던 버전이 있다는 뜻이다 = 갱신 */
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            onUpdate({ waiting: next });
          }
        });
      });
    }).catch(() => { /* 등록 실패해도 앱은 그대로 동작한다 */ });

    let controlled = Boolean(navigator.serviceWorker.controller);
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // 첫 설치의 제어권 획득은 현재 페이지를 교체하는 갱신이 아닙니다.
      if (!controlled) { controlled = true; return; }
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  };

  whenPageReady(start);
}

/**
 * 페이지 로드가 끝난 뒤 실행한다. **이미 끝났으면 기다리지 않는다.**
 *
 * `window.addEventListener("load", ...)` 만 쓰면, 이미 load 가 지나간 뒤에
 * 등록하는 경우 리스너가 영영 호출되지 않는다. React effect 에서 부르는 이
 * 코드가 정확히 그 경우다 — 빠른 환경에서는 지나가 있고 느린 환경에서는 아니라서,
 * 되기도 하고 안 되기도 한다.
 */
export function whenPageReady(run: () => void): void {
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
}

/** 사용자가 "새로고침" 을 눌렀을 때만 넘어간다 — 보던 화면을 마음대로 갈아치우지 않는다. */
export function applyUpdate(u: NonNullable<PwaUpdate>): void {
  u.waiting.postMessage("skip-waiting");
}
